import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn, DatePicker, Flex, Icon, Text } from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1Dictionary,
  V1DictionaryEntry,
  V1HashableValue,
  V1List,
  V1Set,
} from '@/generated/hooks/grimoire/models';
import { BooleanInput } from '@/modules/automation-inputs/components/BooleanInput';
import { FileInput } from '@/modules/automation-inputs/components/file-upload/FileInput';
import { NumberInput } from '@/modules/automation-inputs/components/NumberInput';
import { TextInput } from '@/modules/automation-inputs/components/TextInput';
import { TimeInput } from '@/modules/automation-inputs/components/TimeInput';
import {
  DATE_FORMAT_TOKENS,
  dateToISOString,
  joinDateTimeISO,
  parseDateString,
  splitDateTimeISO,
} from '@/shared/utils/datetime-utils';

import {
  displayConfigValue,
  encodeConfigValue,
  encodeScalarTextEdit,
  getValueType,
  type TConfigValueKind,
} from '@/modules/automation-inputs/utils/config-value';

/**
 * Field corner radius for config editors, per design. 10px is not a lattice
 * radius token (`--radius` is 4px, so sm/md/lg/xl give 4/6/8/12), so it is set
 * explicitly. Applied via descendant selectors because the shared input
 * components (`TextInput`, `NumberInput`, `TimeInput`, …) build their own
 * `className` and don't accept one — this keeps every kind consistent from one
 * place instead of threading a radius prop through seven components.
 */
const FIELD_RADIUS =
  '[&_input]:rounded-[10px] [&_textarea]:rounded-[10px] [&_button]:rounded-[10px] [&_[data-slot=input]]:rounded-[10px]';

/**
 * The file upload surfaces carry their own radius (`rounded-lg` on the dropzone,
 * `rounded-xl` on the card), so they need matching separately from the plain
 * fields above. Scoped to this editor via `[role=button]` (both surfaces are
 * click targets) and the card's existing e2e test id, so the automation-inputs
 * sheet keeps its own look.
 */
const FILE_FIELD_RADIUS =
  '[&_[role=button]]:rounded-[10px] [&_[data-testid^=file-card]]:rounded-[10px]';

interface IScalarConfigEditorProps {
  name: string;
  value?: Commonv1Value;
  kind: TConfigValueKind | undefined;
  onCommit: (value: Commonv1Value) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Render text as an auto-growing textarea (prose, not a label). */
  multiline?: boolean;
  /** Fill the container's height — a full-height editor dialog. */
  fillHeight?: boolean;
  /** Focus on mount. */
  autoFocus?: boolean;
  /**
   * Human-readable field label. `name` is the addressing key (element ids, test
   * ids) and for a params-rail slot that is a raw manifest path, so it must not
   * reach anything the user reads — it was surfacing as both the placeholder
   * ("Enter slot-s3/0/extract_input/llm_model") and the date field's accessible
   * name.
   */
  label?: string;
}

/** A single scalar (or file) config value. Edits stay local (`draft`) and
 *  commit on blur/Enter — Escape reverts the draft without saving. Booleans
 *  and files have no meaningful blur/Enter of their own, so they commit the
 *  moment they change. */
function ScalarConfigEditor({
  name,
  value,
  kind,
  onCommit,
  disabled,
  placeholder,
  multiline,
  fillHeight,
  autoFocus,
  label,
}: IScalarConfigEditorProps) {
  const initialDisplay =
    kind === 'boolean' ? !!value?.bool_value : displayConfigValue(value);
  const [draft, setDraft] = useState<string | boolean>(initialDisplay);

  const commitIfChanged = (next: string | boolean) => {
    if (next === initialDisplay) return;
    // A text edit goes through the shared guards; a boolean comes from a checkbox
    // and is already the right type.
    onCommit(
      typeof next === 'boolean'
        ? encodeConfigValue(next, kind)
        : encodeScalarTextEdit(next, kind),
    );
  };

  if (kind === 'boolean') {
    return (
      <BooleanInput
        name={name}
        checked={draft as boolean}
        onChange={checked => {
          setDraft(checked);
          commitIfChanged(checked);
        }}
        disabled={disabled}
      />
    );
  }

  if (kind === 'file') {
    return (
      <div className={cn('w-full', FILE_FIELD_RADIUS)}>
        <FileInput
          name={name}
          value={draft as string}
          onChange={next => {
            setDraft(next);
            onCommit(encodeConfigValue(next, 'file'));
          }}
          disabled={disabled}
          singleColumn
        />
      </div>
    );
  }

  // Date (and the date half of datetime, below) use the platform's calendar
  // popover — the same `DatePicker` the schedule trigger uses — rather than a
  // native `<input type=date>`, whose rendering varies by browser and doesn't
  // match the rest of the DS. A calendar selection is a discrete action, not
  // free typing, so it commits immediately rather than waiting for a blur.
  if (kind === 'date') {
    const dateText = displayConfigValue(value);
    return (
      <DatePicker
        date={dateText ? parseDateString(dateText) : undefined}
        onDateChange={next =>
          onCommit(
            next
              ? encodeConfigValue(dateToISOString(next), 'date')
              : { null_value: 'NULL_VALUE' },
          )
        }
        placeholder={placeholder}
        disabled={disabled}
        format={DATE_FORMAT_TOKENS.DATE_INPUT}
        className="w-full"
        buttonClassName="w-full"
        aria-label={label ?? name}
        data-testid={`input-${name}`}
      />
    );
  }

  if (kind === 'datetime') {
    const { date: dateHalf, time: timeHalf } = splitDateTimeISO(
      displayConfigValue(value),
    );
    // `parseDateTime` (via `encodeConfigValue`) THROWS on anything but a full
    // `YYYY-MM-DDTHH:mm`, and a two-field editor is half-empty constantly — the
    // moment you pick a date before touching the time. So the date is what makes
    // the value exist: without one there is nothing to store (cleared), and with
    // one a missing time means midnight rather than an exception thrown from an
    // onChange handler.
    const commitDateTime = (nextDate: string, nextTime: string) => {
      if (!nextDate) {
        onCommit({ null_value: 'NULL_VALUE' });
        return;
      }
      onCommit(
        encodeConfigValue(
          joinDateTimeISO(nextDate, nextTime || '00:00'),
          'datetime',
        ),
      );
    };
    return (
      <Flex gap={2} width="full">
        <Flex flex="1" className="min-w-0">
          <DatePicker
            date={dateHalf ? parseDateString(dateHalf) : undefined}
            onDateChange={next =>
              commitDateTime(next ? dateToISOString(next) : '', timeHalf)
            }
            placeholder={placeholder}
            disabled={disabled}
            format={DATE_FORMAT_TOKENS.DATE_INPUT}
            className="w-full"
            buttonClassName="w-full"
            aria-label={label ?? name}
            data-testid={`input-${name}-date`}
          />
        </Flex>
        <Flex flex="1" className="min-w-0">
          <TimeInput
            name={`${name}-time`}
            value={timeHalf}
            onChange={next => commitDateTime(dateHalf, next)}
            disabled={disabled}
          />
        </Flex>
      </Flex>
    );
  }

  const handleBlur = () => commitIfChanged(draft);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // In a multiline field Enter is a newline, not a commit — committing there
    // would make a paragraph-long extraction rule impossible to write. Blur
    // still commits, so nothing is lost.
    if (event.key === 'Enter' && !multiline) {
      (document.activeElement as HTMLElement | null)?.blur();
    } else if (event.key === 'Escape') {
      setDraft(initialDisplay);
    }
  };

  const commonProps = {
    name,
    value: draft as string,
    onChange: (next: string) => setDraft(next),
    disabled,
    // Without this the shared inputs fall back to `t('placeholders.enter',
    // { field: name })` — i.e. the raw slot path.
    placeholder: placeholder ?? label,
  };

  return (
    <div
      // `h-full` on the textarea resolves against this box, so it has to carry
      // the height too — otherwise a percentage height against an auto-height
      // containing block collapses to one line inside the expand dialog.
      className={cn('w-full', fillHeight && 'h-full', FIELD_RADIUS)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      {kind === 'number' && <NumberInput {...commonProps} />}
      {kind === 'time' && <TimeInput {...commonProps} />}
      {kind !== 'number' && kind !== 'time' && (
        <TextInput
          {...commonProps}
          multiline={multiline}
          fillHeight={fillHeight}
          autoFocus={autoFocus}
        />
      )}
    </div>
  );
}

interface ICollectionEditorProps<TValue> {
  name: string;
  value?: TValue;
  onCommit: (value: TValue) => void;
  disabled?: boolean;
}

/** Ordered list of scalar values (`V1List`). Each item keeps its own type
 *  (a list of numbers stays numbers); a newly added item defaults to text.
 *  Row keys include a `version` bumped on add/remove so reordering can't
 *  leave a row's local draft pointing at the wrong item. */
function ListConfigEditor({
  name,
  value,
  onCommit,
  disabled,
}: ICollectionEditorProps<V1List>) {
  const { t: tAutomation } = useTranslation('automation');
  const items = value?.items ?? [];
  const [version, setVersion] = useState(0);

  const commitItems = (nextItems: Commonv1Value[]) =>
    onCommit({ items: nextItems });

  return (
    <Flex direction="column" gap={2} align="stretch" width="full">
      {items.map((item, index) => (
        <Flex key={`${version}-${index}`} align="center" gap={2}>
          <Flex className="min-w-0 flex-1">
            <ScalarConfigEditor
              name={`${name}-item-${index}`}
              value={item}
              kind={getValueType(item) ?? 'text'}
              disabled={disabled}
              placeholder={tAutomation(
                'automation.manifestSopView.config.valuePlaceholder',
              )}
              onCommit={newItem => {
                const next = [...items];
                next[index] = newItem;
                commitItems(next);
              }}
            />
          </Flex>
          <Button
            variant="secondary"
            size="icon-sm"
            disabled={disabled}
            onClick={() => {
              setVersion(v => v + 1);
              commitItems(items.filter((_, i) => i !== index));
            }}
            aria-label={tAutomation(
              'automation.manifestSopView.config.removeItem',
            )}
          >
            <Icon type="X" size="xs" />
          </Button>
        </Flex>
      ))}
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => {
          setVersion(v => v + 1);
          commitItems([...items, { text: '' }]);
        }}
      >
        <Icon type="Plus" size="xs" />
        {tAutomation('automation.manifestSopView.config.addItem')}
      </Button>
    </Flex>
  );
}

/** Unordered collection of unique hashable values (`V1Set`). Behaves like
 *  `ListConfigEditor` — hashable values are a strict subset of `Commonv1Value`
 *  (no file/list/dictionary/table/set), so the same scalar editor applies. */
function SetConfigEditor({
  name,
  value,
  onCommit,
  disabled,
}: ICollectionEditorProps<V1Set>) {
  const { t: tAutomation } = useTranslation('automation');
  const items = value?.items ?? [];
  const [version, setVersion] = useState(0);

  const commitItems = (nextItems: V1HashableValue[]) =>
    onCommit({ items: nextItems });

  return (
    <Flex direction="column" gap={2} align="stretch" width="full">
      {items.map((item, index) => (
        <Flex key={`${version}-${index}`} align="center" gap={2}>
          <Flex className="min-w-0 flex-1">
            <ScalarConfigEditor
              name={`${name}-item-${index}`}
              value={item as Commonv1Value}
              kind={getValueType(item as Commonv1Value) ?? 'text'}
              disabled={disabled}
              placeholder={tAutomation(
                'automation.manifestSopView.config.valuePlaceholder',
              )}
              onCommit={newItem => {
                const next = [...items];
                next[index] = newItem as V1HashableValue;
                commitItems(next);
              }}
            />
          </Flex>
          <Button
            variant="secondary"
            size="icon-sm"
            disabled={disabled}
            onClick={() => {
              setVersion(v => v + 1);
              commitItems(items.filter((_, i) => i !== index));
            }}
            aria-label={tAutomation(
              'automation.manifestSopView.config.removeItem',
            )}
          >
            <Icon type="X" size="xs" />
          </Button>
        </Flex>
      ))}
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => {
          setVersion(v => v + 1);
          commitItems([...items, { text: '' }]);
        }}
      >
        <Icon type="Plus" size="xs" />
        {tAutomation('automation.manifestSopView.config.addItem')}
      </Button>
    </Flex>
  );
}

/** Key/value map (`V1Dictionary`). Keys are always edited as plain text —
 *  dictionary keys are hashable values, but business-tunable dictionaries in
 *  practice use string keys, so a generic key-type editor isn't worth the
 *  complexity. Values keep their own type like list items. */
function DictionaryConfigEditor({
  name,
  value,
  onCommit,
  disabled,
}: ICollectionEditorProps<V1Dictionary>) {
  const { t: tAutomation } = useTranslation('automation');
  const entries = value?.entries ?? [];
  const [version, setVersion] = useState(0);

  const commitEntries = (nextEntries: V1DictionaryEntry[]) =>
    onCommit({ entries: nextEntries });

  return (
    <Flex direction="column" gap={2} align="stretch" width="full">
      {entries.map((entry, index) => (
        <Flex key={`${version}-${index}`} align="center" gap={2}>
          <Flex className="w-1/3 shrink-0">
            <ScalarConfigEditor
              name={`${name}-key-${index}`}
              value={entry.key as Commonv1Value | undefined}
              kind="text"
              disabled={disabled}
              placeholder={tAutomation(
                'automation.manifestSopView.config.keyPlaceholder',
              )}
              onCommit={newKey => {
                const next = [...entries];
                next[index] = { ...entry, key: newKey as V1HashableValue };
                commitEntries(next);
              }}
            />
          </Flex>
          <Flex className="min-w-0 flex-1">
            <ScalarConfigEditor
              name={`${name}-value-${index}`}
              value={entry.value}
              kind={getValueType(entry.value) ?? 'text'}
              disabled={disabled}
              placeholder={tAutomation(
                'automation.manifestSopView.config.valuePlaceholder',
              )}
              onCommit={newValue => {
                const next = [...entries];
                next[index] = { ...entry, value: newValue };
                commitEntries(next);
              }}
            />
          </Flex>
          <Button
            variant="secondary"
            size="icon-sm"
            disabled={disabled}
            onClick={() => {
              setVersion(v => v + 1);
              commitEntries(entries.filter((_, i) => i !== index));
            }}
            aria-label={tAutomation(
              'automation.manifestSopView.config.removeItem',
            )}
          >
            <Icon type="X" size="xs" />
          </Button>
        </Flex>
      ))}
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => {
          setVersion(v => v + 1);
          commitEntries([
            ...entries,
            { key: { text: '' }, value: { text: '' } },
          ]);
        }}
      >
        <Icon type="Plus" size="xs" />
        {tAutomation('automation.manifestSopView.config.addItem')}
      </Button>
    </Flex>
  );
}

export interface IManifestConfigValueEditorProps {
  name: string;
  value?: Commonv1Value;
  onCommit: (value: Commonv1Value) => void;
  disabled?: boolean;
  /**
   * Render a text value as an auto-growing textarea. Opt-in per call site,
   * because nothing in a `text` value itself says whether it's a label or a
   * paragraph — the step workbench's params rail sets it for rule-shaped slots.
   */
  multiline?: boolean;
  /** Fill the container's height — for a full-height editor dialog. */
  fillHeight?: boolean;
  /** Focus the value editor on mount — for an editor that IS a dialog. */
  autoFocus?: boolean;
  /**
   * Human-readable field label, for the accessible name and placeholder. `name`
   * is the addressing key (ids, test ids) and may be a raw manifest path, so it
   * must not reach anything the user reads.
   */
  label?: string;
}

/** Dispatches a config value to the right editor by its populated
 *  `Commonv1Value` variant. `table` has no editor yet — it renders read-only. */
export function ManifestConfigValueEditor({
  name,
  value,
  onCommit,
  disabled,
  multiline,
  fillHeight,
  autoFocus,
  label,
}: IManifestConfigValueEditorProps) {
  const { t: tAutomation } = useTranslation('automation');
  const kind = getValueType(value);

  if (kind === 'list') {
    return (
      <div className={cn('w-full', FIELD_RADIUS)}>
        <ListConfigEditor
          name={name}
          value={value?.list}
          disabled={disabled}
          onCommit={list => onCommit({ list })}
        />
      </div>
    );
  }
  if (kind === 'set') {
    return (
      <div className={cn('w-full', FIELD_RADIUS)}>
        <SetConfigEditor
          name={name}
          value={value?.set}
          disabled={disabled}
          onCommit={set => onCommit({ set })}
        />
      </div>
    );
  }
  if (kind === 'dictionary') {
    return (
      <div className={cn('w-full', FIELD_RADIUS)}>
        <DictionaryConfigEditor
          name={name}
          value={value?.dictionary}
          disabled={disabled}
          onCommit={dictionary => onCommit({ dictionary })}
        />
      </div>
    );
  }
  if (kind === 'table') {
    // No editor exists for `table` yet, but it still has to read as a FIELD —
    // bare muted text next to the label was easy to miss entirely. A bordered
    // `bg-muted` box reads as inert without needing a hardcoded surface colour
    // (which broke in dark mode), so a read-only value doesn't look absent.
    //
    // Deliberately NOT `displayConfigValue`: a table's rows are feather-v2 binary
    // in `inline.data`, so that returns the wire object as multi-line JSON — a
    // base64 blob, or `{}` for an empty table. The table's own name (the last
    // segment of a remote reference) is the only part worth showing.
    const tableName = value?.table?.remote?.split('/').filter(Boolean).pop();
    return (
      <Flex
        align="center"
        width="full"
        className="h-9 rounded-[10px] border border-input bg-muted px-3 py-1"
      >
        <Text level="small" color="muted" className="truncate">
          {tableName ||
            tAutomation('automation.manifestSopView.config.tableValue')}
        </Text>
      </Flex>
    );
  }

  return (
    <div className={cn('w-full', fillHeight && 'h-full', FIELD_RADIUS)}>
      <ScalarConfigEditor
        name={name}
        value={value}
        kind={kind}
        disabled={disabled}
        multiline={multiline}
        fillHeight={fillHeight}
        autoFocus={autoFocus}
        label={label}
        onCommit={onCommit}
      />
    </div>
  );
}
